import { LitElement, html, css, nothing } from 'lit';
import '../katapult-elements/katapult-icon.js';
import '../katapult-elements/katapult-button.js';
import '@polymer/paper-menu-button/paper-menu-button.js';
import { checkIsInternalOrigin } from '../../modules/CheckOrigin.js';

export const VERSION_CONFIGS = [
  {
    name: 'alpha',
    label: 'Alpha Release',
    description: 'Preview for features hot off the press.',
    color: '#FF8300',
    icon: 'mode_heat'
  },
  {
    name: 'beta',
    label: 'Beta Release',
    description: 'A stable preview of the next release.',
    color: '#007299',
    icon: 'lightbulb_2'
  },
  {
    name: 'release',
    label: 'Current Release',
    description: 'The latest release of Katapult Pro, recommended for all users.',
    color: 'var(--paper-green-500)',
    icon: 'new_releases'
  },
  {
    name: 'legacy',
    label: 'Legacy Release',
    description: 'The previous version of Katapult Pro, not recommended for daily use.',
    color: '#717271',
    icon: 'history'
  }
];

class VersionSelectorButton extends LitElement {
  static styles = css`
    #versionSelector {
      padding: 0px;
    }
    #versionSelectorDrawer {
      padding: 0px;
    }
    .version-selector-row {
      box-sizing: border-box;
      min-width: 100%;
      display: flex;
      flex-direction: column;
      padding: 16px;
      gap: 8px;
    }
    .version-selector-row:hover {
      cursor: pointer;
      background-color: var(--paper-grey-200);
    }
    .version-selector-row-header {
      display: flex;
      align-items: center;
      font-weight: bold;
      gap: 8px;
    }
    .version-selector-row-header > div {
      font-size: 16px;
    }
    .recommended-version-label {
      font-weight: normal;
      color: var(--primary-text-color-faded);
    }
  `;

  render() {
    return html`
      <paper-menu-button id="versionSelector" horizontal-align="left" vertical-align="top" no-overlap>
        <katapult-button
          slot="dropdown-trigger"
          outlined
          color="${this.currentVersionConfig.color}"
          icon="${this.currentVersionConfig.icon}"
          title="Click to view other available versions of Katapult Pro"
        >
          <span style="margin-right: 4px;">Using ${this.currentVersionConfig.label}</span>
          <katapult-icon icon="arrow_drop_down"></katapult-icon>
        </katapult-button>
        <div id="versionSelectorDrawer" slot="dropdown-content">
          ${VERSION_CONFIGS.filter(({ name }) => this.currentVersionName !== name && (!this.isInternalOrigin || name !== 'legacy')).map(
            (versionConfig) => html`
              <div class="version-selector-row" data-version-name="${versionConfig.name}" @click="${this.versionSelected}">
                <div class="version-selector-row-header">
                  <katapult-icon icon="${versionConfig.icon}"></katapult-icon>
                  <div>
                    Switch to <span style="color: ${versionConfig.color}">${versionConfig.label}</span>
                    ${versionConfig.name == 'release' ? html`<span class="recommended-version-label">(Recommended)</span>` : nothing}
                  </div>
                </div>
                <div>${versionConfig.description}</div>
              </div>
            `
          )}
        </div>
      </paper-menu-button>
    `;
  }

  static properties = {
    currentVersionName: { type: String },
    isInternalOrigin: { type: Boolean }
  };

  constructor() {
    super();

    this.currentVersionName = null;
    this.currentVersionConfig = {};
    this.isInternalOrigin = checkIsInternalOrigin();
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('currentVersionName')) {
      this.currentVersionConfig = VERSION_CONFIGS.find((config) => config.name === this.currentVersionName) ?? {};
    }
  }

  versionSelected(event) {
    const { versionName } = event.currentTarget.dataset;
    // Get the new subdomain for the version (de-aliasing for internal environments)
    const targetVersionSubdomain = this.isInternalOrigin ? { alpha: 'dev', beta: 'rc', release: 'main' }[versionName] : versionName;
    const url = new URL(window.location.href);
    const baseHostname = url.hostname.replace(/^(alpha|beta|legacy|dev|rc|main|prev|next)\./, '');

    // Set the new hostname
    url.hostname = targetVersionSubdomain === 'release' ? baseHostname : `${targetVersionSubdomain}.${baseHostname}`;
    // Release doesn't need a subdomain
    window.location.href = url.href;
  }
}

customElements.define('version-selector-button', VersionSelectorButton);
